import { useRef, useMemo, useEffect, useCallback, RefObject, useImperativeHandle, ForwardedRef } from 'react';
import type {
    Annotation,
    PointAnnotationPathData,
    AnnotationPathDataById,
    AnnotationStyle,
    Coordinates,
    StyleDataById,
    AnnotationPathData,
    PartialAnnotationStyle,
    AnnotationIdWithStyleData,
    Geometry,
    Segment,
} from './models';
import defaultStyle from './style/defaultStyleOptions';
import {
    areCoordinatesInsideCircle,
    drawAnnotations,
    drawCurrentAnnotation,
    getMiddlePoint,
    getSegmentsToDraw,
    overloadStyle,
} from './utils';

interface UseAnnotationEngineArgs {
    annotations: Annotation[];
    annotationToEdit?: Annotation;
    onEvent: OnEvent;
    canvasRef: RefObject<HTMLCanvasElement>;
    ref: ForwardedRef<Handles>;
    styleForAnnotations: StyleDataById;
    styleForAnnotationToEdit?: PartialAnnotationStyle;
    styleForPointsToEdit?: StyleDataById;
}

export interface Handles {
    cancelCreation: () => void;
}

export type PointId = number;
export type MouseEvents =
    | MouseMoveEvents
    | MouseDownEvent
    | MouseDownOnExistingPointEvent
    | MouseDownOnAnnotationEvent
    | MouseWheelEvent
    | MouseUpEvent
    | MouseUpOnExistingPointEvent
    | MouseDoubleClickOnAnnotationEvent
    | MouseDoubleClickEvent
    | contextMenuEvent;

export type MouseMoveEvents = MouseMoveOnAnnotationEvent | MouseMoveEvent | MouseMoveOnExistingPointEvent;

export type KeyboardEvents = KeyUpEvent | KeyDownEvent;

export type Events = MouseEvents | KeyboardEvents;

export type OnEvent = (event: Events, operations: Operations) => void;
export type PointIds = Array<PointId>;

export interface MouseDownEvent {
    type: 'mouse_down_event';
    at: Coordinates;
    currentGeometry: Geometry;
    event: MouseEvent;
}

export interface MouseDownOnExistingPointEvent {
    type: 'mouse_down_on_existing_point_event';
    at: Coordinates;
    pointIds: PointIds;
    currentGeometry: Geometry;
    event: MouseEvent;
}

export interface MouseDownOnAnnotationEvent {
    type: 'mouse_down_on_annotation_event';
    at: Coordinates;
    annotationsId: string[];
    pointIds: PointIds;
    currentGeometry: Geometry;
    event: MouseEvent;
}

export interface MouseMoveOnAnnotationEvent {
    type: 'mouse_move_on_annotation_event';
    at: Coordinates;
    currentGeometry: Geometry;
    annotationsIdsWithStyle: AnnotationIdWithStyleData[];
    pointIds: PointIds;
    event: MouseEvent;
}

export interface MouseMoveOnExistingPointEvent {
    type: 'mouse_move_on_existing_point_event';
    at: Coordinates;
    pointIds: PointIds;
    currentGeometry: Geometry;
    event: MouseEvent;
}

export interface MouseMoveEvent {
    type: 'mouse_move_event';
    to: Coordinates;
    currentGeometry: Geometry;
    event: MouseEvent;
}

export interface MouseUpEvent {
    type: 'mouse_up_event';
    at: Coordinates;
    currentGeometry: Geometry;
    event: MouseEvent;
}

export interface MouseUpOnExistingPointEvent {
    type: 'mouse_up_on_existing_point_event';
    at: Coordinates;
    pointIds: PointIds;
    currentGeometry: Geometry;
    event: MouseEvent;
}

export interface MouseDoubleClickOnAnnotationEvent {
    type: 'mouse_double_click_on_annotation_event';
    at: Coordinates;
    event: MouseEvent;
    annotationsId: string[];
    currentGeometry: Geometry;
}

export interface MouseDoubleClickEvent {
    type: 'mouse_double_click_event';
    event: MouseEvent;
    currentGeometry: Geometry;
}

export interface MouseWheelEvent {
    type: 'mouse_wheel_event';
    deltaX: number;
    deltaY: number;
    event: WheelEvent;
}

export interface KeyUpEvent {
    type: 'key_up_event';
    currentGeometry: Geometry;
    event: KeyboardEvent;
}

export interface KeyDownEvent {
    type: 'key_down_event';
    currentGeometry: Geometry;
    event: KeyboardEvent;
}

export interface contextMenuEvent {
    type: 'context_menu_event';
    event: MouseEvent;
}

export interface Operations {
    addPoint(at: Coordinates): PointId;
    insertPoint(at: Coordinates, index: number): void;
    movePoint(pointId: PointId, to: Coordinates): void;
    finishCurrentLine(): void;
    drawOnCanvas(draw: (context2d: CanvasRenderingContext2D) => void): void;
    deleteLastPoint(): number;
    deletePoint(pointId: PointId): void;
}

const useAnnotationEngine = ({
    annotationToEdit,
    annotations,
    onEvent,
    canvasRef,
    ref,
    styleForAnnotations,
    styleForAnnotationToEdit,
    styleForPointsToEdit,
}: UseAnnotationEngineArgs): void => {
    const renderingContextRef = useRef<CanvasRenderingContext2D | undefined>(undefined);
    const annotationToEditPointsRef = useRef<Coordinates[]>([]);
    const annotationsPathsRef = useRef<AnnotationPathDataById>(new Map());
    const annotationToEditStyleRef = useRef<AnnotationStyle>(defaultStyle);
    const EXISTING_POINT_RADIUS_DETECTION = 8;

    const canvasCoordinateOf = (canvas: HTMLCanvasElement, event: MouseEvent): Coordinates => {
        const rect = canvas.getBoundingClientRect();

        return {
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
        };
    };

    const getMatchingAnnotationsId = (
        annotationsPathsMap: AnnotationPathDataById,
        { x, y }: Coordinates,
        renderingContext?: CanvasRenderingContext2D,
    ): string[] => {
        const matchingAnnotationsId: string[] = [];
        const isPointInPath = (path: Path2D) => renderingContext?.isPointInPath(path, x, y);
        const isPointAnnotation = (pathData: AnnotationPathData): pathData is PointAnnotationPathData =>
            (pathData as PointAnnotationPathData).point !== undefined;
        const isPointInShapeOrLabel = (pathData: AnnotationPathData): boolean => {
            if (isPointInPath(pathData.label)) {
                return true;
            }

            if (isPointAnnotation(pathData) && isPointInPath(pathData.point)) {
                return true;
            }

            if (!isPointAnnotation(pathData)) {
                return pathData.lines.some((line) => {
                    return renderingContext?.isPointInPath(line, x, y) || renderingContext?.isPointInStroke(line, x, y);
                });
            }

            return false;
        };

        annotationsPathsMap.forEach((annotationPaths, annotationId) => {
            if (isPointInShapeOrLabel(annotationPaths)) {
                matchingAnnotationsId.push(annotationId);
            }
        });

        return matchingAnnotationsId;
    };

    const excludeLastPointFromDetectionIfNoAnnotationToEdit = (coordinates: Geometry) => {
        if (!annotationToEdit) {
            coordinates.pop();
        }
    };
    const getMiddlePointsFromAnnotationToEdit = (): Coordinates[] => {
        if (!annotationToEdit || !annotationToEdit.hasMiddlePoints) {
            return [];
        }

        const segments = getSegmentsToDraw(annotationToEdit.coordinates, annotationToEdit.isClosed);
        const middlePoints = segments.map((segment: Segment): Coordinates => getMiddlePoint(segment));

        return middlePoints;
    };
    const getMatchedExistingPointIds = (coordinates: Geometry, moveOn: Coordinates): PointIds => {
        const middlePoints = getMiddlePointsFromAnnotationToEdit();
        const coords = [...coordinates];
        excludeLastPointFromDetectionIfNoAnnotationToEdit(coords);
        coords.push(...middlePoints);

        return coords
            .map((coordinate, idx) => ({ coordinate, idx }))
            .filter(({ coordinate }) => areCoordinatesInsideCircle(coordinate, moveOn, EXISTING_POINT_RADIUS_DETECTION))
            .map(({ idx }) => idx);
    };

    useEffect(() => {
        annotationToEditStyleRef.current = overloadStyle(defaultStyle, styleForAnnotationToEdit);
    }, [styleForAnnotationToEdit]);

    useEffect(() => {
        annotationToEditPointsRef.current = [];
        annotationsPathsRef.current.forEach((_annotationPath: AnnotationPathData, id: string) => {
            if (!annotations.some((anno) => anno.id === id)) {
                annotationsPathsRef.current.delete(id);
            }
        });

        if (annotationToEdit) {
            annotationToEditPointsRef.current = annotationToEdit.coordinates;
        } else {
            annotationToEditPointsRef.current = [];
        }
    }, [annotationToEdit, annotations]);

    const annotationsToDraw = useMemo<Annotation[]>(() => {
        if (annotationToEdit) {
            const annotationToEditIndex = annotations.findIndex(
                (annotation: Annotation) => annotation.id === annotationToEdit.id,
            );

            if (annotationToEditIndex >= 0) {
                return [
                    ...annotations.slice(0, annotationToEditIndex),
                    ...annotations.slice(annotationToEditIndex + 1),
                ];
            }
        }

        return [...annotations];
    }, [annotations, annotationToEdit]);

    const drawScene = useCallback(() => {
        const currentCanvasRef = canvasRef.current;

        if (!renderingContextRef.current || !currentCanvasRef) {
            return;
        }

        renderingContextRef.current.clearRect(0, 0, currentCanvasRef.width, currentCanvasRef.height);

        drawAnnotations(
            renderingContextRef.current,
            annotationsToDraw,
            styleForAnnotations,
            annotationsPathsRef.current,
        );

        drawCurrentAnnotation(
            renderingContextRef.current,
            annotationToEditPointsRef.current,
            annotationToEditStyleRef.current,
            styleForPointsToEdit,
            annotationToEdit,
        );
    }, [annotationsToDraw, canvasRef, annotationToEdit, styleForAnnotations, styleForPointsToEdit]);

    useImperativeHandle(ref, () => ({
        cancelCreation() {
            if (annotationToEdit === undefined) {
                annotationToEditPointsRef.current = [];
                drawScene();
            }
        },
    }));

    // Initialize canvas
    useEffect(() => {
        const currentCanvasRef = canvasRef.current;
        let delayDraw: Array<(context2d: CanvasRenderingContext2D) => void> = [];
        const operations: Operations = {
            // -1 is here because he return also the index of the array of points
            addPoint: (at: Coordinates): PointId => annotationToEditPointsRef.current.push(at) - 1,
            insertPoint: (at: Coordinates, index: number): void => {
                annotationToEditPointsRef.current.splice(index, 0, at);
            },
            movePoint: (pointId: PointId, to: Coordinates): void => {
                annotationToEditPointsRef.current[pointId] = to;
            },
            finishCurrentLine: (): void => {
                annotationToEditPointsRef.current = [];
            },
            drawOnCanvas: (draw: (context2d: CanvasRenderingContext2D) => void): void => {
                delayDraw.push(draw);
            },
            deleteLastPoint: (): number => {
                annotationToEditPointsRef.current.splice(annotationToEditPointsRef.current.length - 2, 1);

                return annotationToEditPointsRef.current.length - 1;
            },
            deletePoint: (pointId: PointId): void => {
                annotationToEditPointsRef.current.splice(pointId, 1);
            },
        };

        const handleEvent = (handler: (canvas: HTMLCanvasElement) => void) => {
            if (currentCanvasRef) {
                handler(currentCanvasRef);
                drawScene();
                const context2d = currentCanvasRef.getContext('2d');
                if (context2d) {
                    delayDraw.forEach((draw) => draw(context2d));
                }
                delayDraw = [];
            }
        };

        const handleDoubleClick = (event: MouseEvent) =>
            handleEvent((canvas) => {
                event.preventDefault();
                const eventCoords = canvasCoordinateOf(canvas, event);
                const renderingContext = renderingContextRef.current;

                const matchingAnnotationsId = getMatchingAnnotationsId(
                    annotationsPathsRef.current,
                    eventCoords,
                    renderingContext,
                );

                if (matchingAnnotationsId.length) {
                    onEvent(
                        {
                            type: 'mouse_double_click_on_annotation_event',
                            at: eventCoords,
                            currentGeometry: [...annotationToEditPointsRef.current],
                            annotationsId: matchingAnnotationsId,
                            event,
                        },
                        operations,
                    );
                }

                return onEvent(
                    {
                        type: 'mouse_double_click_event',
                        event,
                        currentGeometry: [...annotationToEditPointsRef.current],
                    },
                    operations,
                );
            });

        const handleMouseUp = (event: MouseEvent) =>
            handleEvent((canvas) => {
                event.preventDefault();
                const eventCoords = canvasCoordinateOf(canvas, event);
                const clickedPointIds = getMatchedExistingPointIds(annotationToEditPointsRef.current, eventCoords);
                if (clickedPointIds.length > 0) {
                    onEvent(
                        {
                            type: 'mouse_up_on_existing_point_event',
                            at: eventCoords,
                            pointIds: clickedPointIds,
                            currentGeometry: [...annotationToEditPointsRef.current],
                            event,
                        },
                        operations,
                    );
                } else {
                    const currentGeometry = [...annotationToEditPointsRef.current];
                    onEvent(
                        {
                            type: 'mouse_up_event',
                            at: eventCoords,
                            currentGeometry,
                            event,
                        },
                        operations,
                    );
                }
            });

        const handleMouseDown = (event: MouseEvent) =>
            handleEvent((canvas) => {
                event.preventDefault();
                const eventCoords = canvasCoordinateOf(canvas, event);
                const renderingContext = renderingContextRef.current;

                const matchingAnnotationsId = getMatchingAnnotationsId(
                    annotationsPathsRef.current,
                    eventCoords,
                    renderingContext,
                );

                const clickedPointIds = getMatchedExistingPointIds(annotationToEditPointsRef.current, eventCoords);

                if (matchingAnnotationsId.length && !annotationToEdit) {
                    return onEvent(
                        {
                            type: 'mouse_down_on_annotation_event',
                            at: eventCoords,
                            event,
                            pointIds: clickedPointIds,
                            annotationsId: matchingAnnotationsId,
                            currentGeometry: [...annotationToEditPointsRef.current],
                        },
                        operations,
                    );
                }

                if (clickedPointIds.length > 0) {
                    return onEvent(
                        {
                            type: 'mouse_down_on_existing_point_event',
                            at: eventCoords,
                            pointIds: clickedPointIds,
                            currentGeometry: [...annotationToEditPointsRef.current],
                            event,
                        },
                        operations,
                    );
                }

                return onEvent(
                    {
                        type: 'mouse_down_event',
                        at: eventCoords,
                        currentGeometry: [...annotationToEditPointsRef.current],
                        event,
                    },
                    operations,
                );
            });

        const handleMouseMove = (event: MouseEvent) =>
            handleEvent((canvas) => {
                event.preventDefault(); // prevents from dragging the canvas around when focused by a double click
                const eventCoords = canvasCoordinateOf(canvas, event);

                const isMoveOnExistingPointsIdx = getMatchedExistingPointIds(
                    annotationToEditPointsRef.current,
                    eventCoords,
                );

                if (isMoveOnExistingPointsIdx.length > 0) {
                    return onEvent(
                        {
                            type: 'mouse_move_on_existing_point_event',
                            at: eventCoords,
                            pointIds: isMoveOnExistingPointsIdx,
                            currentGeometry: [...annotationToEditPointsRef.current],
                            event,
                        },
                        operations,
                    );
                }

                const renderingContext = renderingContextRef.current;
                const matchingAnnotationsId = getMatchingAnnotationsId(
                    annotationsPathsRef.current,
                    eventCoords,
                    renderingContext,
                );

                if (matchingAnnotationsId.length) {
                    const annotationsIdsWithStyle = matchingAnnotationsId.map((id) => {
                        const style = styleForAnnotations.get(id);

                        return { id, style };
                    });

                    return onEvent(
                        {
                            type: 'mouse_move_on_annotation_event',
                            at: eventCoords,
                            currentGeometry: [...annotationToEditPointsRef.current],
                            event,
                            annotationsIdsWithStyle,
                            pointIds: isMoveOnExistingPointsIdx,
                        },
                        operations,
                    );
                }

                return onEvent(
                    {
                        type: 'mouse_move_event',
                        to: eventCoords,
                        currentGeometry: [...annotationToEditPointsRef.current],
                        event,
                    },
                    operations,
                );
            });

        const handleMouseWheel = (event: WheelEvent) =>
            handleEvent(() => {
                onEvent(
                    {
                        type: 'mouse_wheel_event',
                        deltaX: event.deltaX,
                        deltaY: event.deltaY,
                        event,
                    },
                    operations,
                );
            });

        const handleKeyUp = (event: KeyboardEvent) =>
            handleEvent(() => {
                onEvent(
                    {
                        type: 'key_up_event',
                        currentGeometry: [...annotationToEditPointsRef.current],
                        event,
                    },
                    operations,
                );
            });

        const handleKeyDown = (event: KeyboardEvent) =>
            handleEvent(() => {
                onEvent(
                    {
                        type: 'key_down_event',
                        currentGeometry: [...annotationToEditPointsRef.current],
                        event,
                    },
                    operations,
                );
            });

        const handleContextMenu = (event: MouseEvent) =>
            handleEvent(() => {
                onEvent(
                    {
                        type: 'context_menu_event',
                        event,
                    },
                    operations,
                );
            });
        if (currentCanvasRef) {
            const canvasRenderingContext = currentCanvasRef.getContext('2d');

            if (canvasRenderingContext) {
                currentCanvasRef.addEventListener('mousedown', handleMouseDown);
                currentCanvasRef.addEventListener('mouseup', handleMouseUp);
                currentCanvasRef.addEventListener('dblclick', handleDoubleClick);
                currentCanvasRef.addEventListener('mousemove', handleMouseMove);
                currentCanvasRef.addEventListener('wheel', handleMouseWheel, { passive: true });
                document.addEventListener('keyup', handleKeyUp);
                document.addEventListener('keydown', handleKeyDown);
                currentCanvasRef.addEventListener('contextmenu', handleContextMenu);
                currentCanvasRef.width = currentCanvasRef.offsetWidth;
                currentCanvasRef.height = currentCanvasRef.offsetHeight;
                renderingContextRef.current = canvasRenderingContext;

                drawScene();
            }
        }

        return () => {
            if (currentCanvasRef) {
                currentCanvasRef.removeEventListener('mouseup', handleMouseUp);
                currentCanvasRef.removeEventListener('dblclick', handleDoubleClick);
                currentCanvasRef.removeEventListener('mousedown', handleMouseDown);
                currentCanvasRef.removeEventListener('mousemove', handleMouseMove);
                currentCanvasRef.removeEventListener('wheel', handleMouseWheel);
                currentCanvasRef.removeEventListener('contextmenu', handleContextMenu);
                document.removeEventListener('keyup', handleKeyUp);
                document.removeEventListener('keydown', handleKeyDown);
            }
        };
    }, [drawScene, canvasRef, onEvent, annotations, styleForAnnotations, annotationToEdit, styleForPointsToEdit]);
};

export default useAnnotationEngine;
